import { useState, useEffect, useRef, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
//  DIFFICULTY CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const DIFFICULTIES = {
  easy: {
    key: "easy",
    label: "Easy",
    si: "පහසු",
    emoji: "😊",
    pots: 3,
    peekMs: 2500,       // how long prize is shown
    blindMs: 0,         // no blindfold on easy
    swapCount: 4,       // how many swaps during shuffle
    swapSpeed: 620,     // ms per swap
    potSwayAmp: 8,      // how much pots sway (degrees)
    rounds: 5,
    color: "#4CAF50",
    bg: "linear-gradient(180deg,#87CEEB 0%,#a8e6a8 45%,#4a8a2a 100%)",
    scorePerHit: 100,
    hint: "3 pots · Slow shuffle · No blindfold",
    hintSi: "මුට්ටි 3 · සෙමින් · ඇස් ඇරෙන්",
  },
  medium: {
    key: "medium",
    label: "Medium",
    si: "මධ්‍යම",
    emoji: "😤",
    pots: 5,
    peekMs: 1800,
    blindMs: 1200,
    swapCount: 7,
    swapSpeed: 430,
    potSwayAmp: 14,
    rounds: 6,
    color: "#FF9800",
    bg: "linear-gradient(180deg,#f5c842 0%,#f0a030 35%,#5a8a2a 100%)",
    scorePerHit: 200,
    hint: "5 pots · Faster shuffle · Blindfolded!",
    hintSi: "මුට්ටි 5 · වේගයෙන් · ඇස් බැඳ",
  },
  hard: {
    key: "hard",
    label: "Hard",
    si: "දුෂ්කර",
    emoji: "😈",
    pots: 7,
    peekMs: 1000,
    blindMs: 2000,
    swapCount: 12,
    swapSpeed: 250,
    potSwayAmp: 20,
    rounds: 7,
    color: "#F44336",
    bg: "linear-gradient(180deg,#1a0a2e 0%,#3d1a0a 40%,#1a2a0a 100%)",
    scorePerHit: 350,
    hint: "7 pots · Chaos shuffle · Long blindfold!",
    hintSi: "මුට්ටි 7 · ව්‍යාකූල · දිගු ඇස් බැඳීම!",
  },
};

const GAME_W = 400;
const GAME_H = 640;
const BEAM_Y = 22;
const ROPE_LEN = 115;
const POT_H = 50;
const STICK_LEN = 108;
const PLAYER_X = GAME_W / 2;
const PLAYER_Y = 390;
const PIVOT_X = PLAYER_X - 8;
const PIVOT_Y = PLAYER_Y - 28;
const HIT_R = 34;
const MAX_SWING = 58; // degrees each side

// pot X positions for each count
const POT_XS = {
  3: [80, 200, 320],
  5: [50, 130, 200, 270, 350],
  7: [35, 95, 155, 200, 245, 305, 365],
};

const sleep = ms => new Promise(r => setTimeout(r, ms));
let _uid = 0;
const uid = () => ++_uid;
const rand = (a, b) => Math.random() * (b - a) + a;

// ─────────────────────────────────────────────────────────────────────────────
//  CSS
// ─────────────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Lilita+One&family=Noto+Sans+Sinhala:wght@700;900&family=Exo+2:wght@400;700;900&display=swap');

*, *::before, *::after { box-sizing:border-box; margin:0; padding:0; -webkit-tap-highlight-color:transparent; user-select:none; }
body { background:#000; overflow:hidden; width:100vw; height:100dvh; }

@keyframes heartbeat   { 0%,100%{transform:scale(1)}    50%{transform:scale(1.2)} }
@keyframes floatUp     { 0%{transform:translateY(0) scale(1);opacity:1} 100%{transform:translateY(-80px) scale(1.3);opacity:0} }
@keyframes stampIn     { 0%{transform:scale(2.2) rotate(-10deg);opacity:0} 65%{transform:scale(.88) rotate(2deg)} 100%{transform:scale(1) rotate(0);opacity:1} }
@keyframes slideUp     { from{transform:translateY(28px);opacity:0} to{transform:translateY(0);opacity:1} }
@keyframes cloudDrift  { 0%{transform:translateX(-20px)} 100%{transform:translateX(20px)} }
@keyframes treeWave    { 0%,100%{transform:rotate(-1.5deg)} 50%{transform:rotate(1.5deg)} }
@keyframes glowPulse   { 0%,100%{filter:drop-shadow(0 0 5px #FFD700)} 50%{filter:drop-shadow(0 0 18px #FFD700) drop-shadow(0 0 34px #FF6B35)} }
@keyframes confettiFall{ 0%{transform:translateY(-10px) rotate(0);opacity:1} 100%{transform:translateY(180px) rotate(540deg);opacity:0} }
@keyframes potBreak    { 0%{transform:scale(1);opacity:1} 40%{transform:scale(1.7);opacity:.8} 100%{transform:scale(3);opacity:0} }
@keyframes potShake    { 0%,100%{transform:translateX(0)} 25%{transform:translateX(-5px)} 75%{transform:translateX(5px)} }
@keyframes blindFlash  { 0%,100%{opacity:.9} 50%{opacity:1} }
@keyframes prizeFloat  { 0%{transform:translate(-50%,-50%) scale(0) rotate(-20deg);opacity:0} 60%{transform:translate(-50%,-60%) scale(1.2) rotate(5deg);opacity:1} 100%{transform:translate(-50%,-80%) scale(1) rotate(0);opacity:0} }
@keyframes correctRing { 0%{transform:scale(.3);opacity:1} 100%{transform:scale(2.5);opacity:0} }
@keyframes diffCardHov { from{transform:scale(1)} to{transform:scale(1.03)} }
@keyframes timerTick   { 0%,100%{opacity:1} 50%{opacity:.5} }
@keyframes swingWobble { 0%,100%{transform:rotate(0deg)} 50%{transform:rotate(-5deg)} }

.root {
  width:100vw; height:100dvh;
  display:flex; align-items:center; justify-content:center;
  background:#000; overflow:hidden; position:relative;
}

/* ── CANVAS ── */
.canvas {
  width: min(100vw, 420px);
  height: 100dvh;
  position:relative; overflow:hidden;
  font-family:'Exo 2', sans-serif;
}

/* ── HUD ── */
.hud {
  position:absolute; top:0; left:0; right:0; z-index:60;
  padding:.55rem .8rem .35rem;
  background:linear-gradient(to bottom,rgba(0,0,0,.72) 70%,transparent);
  display:flex; align-items:center; gap:.4rem;
}
.hud-score {
  background:rgba(255,215,0,.12); border:1px solid rgba(255,215,0,.28);
  border-radius:10px; padding:.2rem .6rem; flex:1;
}
.hud-slbl { font-size:.44rem; color:rgba(255,215,0,.55); letter-spacing:2px; }
.hud-sval { font-size:1.45rem; font-weight:900; color:#FFD700; line-height:1; font-family:'Lilita One',cursive; }
.hud-lives { display:flex; gap:.15rem; align-items:center; }
.hud-right {
  background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.15);
  border-radius:10px; padding:.2rem .55rem; text-align:center;
}
.hud-rlbl { font-size:.42rem; color:rgba(255,255,255,.38); letter-spacing:2px; }
.hud-rval { font-size:.88rem; color:#fff; font-weight:700; line-height:1; }
.streak-badge {
  background:rgba(255,107,53,.18); border:1px solid rgba(255,107,53,.35);
  border-radius:10px; padding:.2rem .5rem; text-align:center;
}
.streak-v { font-size:.9rem; color:#FF6B35; font-weight:900; line-height:1; }

/* ── PHASE BANNERS ── */
.phase-banner {
  position:absolute; left:50%; transform:translateX(-50%);
  z-index:65; white-space:nowrap;
  display:flex; flex-direction:column; align-items:center; gap:.2rem;
  pointer-events:none;
}
.pb-main {
  font-family:'Lilita One',cursive;
  padding:.35rem 1.4rem; border-radius:24px;
  font-size:clamp(.9rem,4vw,1.15rem); letter-spacing:2px;
  box-shadow:0 4px 16px rgba(0,0,0,.5);
  animation:slideUp .3s ease-out both;
}
.pb-si {
  font-family:'Noto Sans Sinhala',sans-serif; font-weight:700;
  font-size:.78rem; color:rgba(255,255,255,.75);
  animation:slideUp .3s .1s ease-out both;
}

/* ── BLINDFOLD SCREEN ── */
.blind-screen {
  position:absolute; inset:0; z-index:70;
  background:rgba(0,0,0,.88);
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:.7rem; animation:blindFlash 1.4s ease-in-out infinite;
}
.blind-icon { font-size:3.5rem; }
.blind-txt  { font-family:'Lilita One',cursive; color:#FFD700; font-size:1.5rem; letter-spacing:3px; text-shadow:0 0 16px #FFD700; }
.blind-si   { font-family:'Noto Sans Sinhala',sans-serif; color:rgba(255,215,0,.65); font-size:1rem; }
.blind-timer{ font-family:'Lilita One',cursive; font-size:2.2rem; color:#FF6B35; animation:timerTick 1s infinite; }

/* ── SWING BUTTON ── */
.swing-btn {
  position:absolute; bottom:22px; left:50%; transform:translateX(-50%);
  background:linear-gradient(135deg,#FFD700,#FF6B35);
  color:#1a0010; border:none; border-radius:50px;
  padding:.85rem 2.8rem; font-size:1.25rem;
  font-family:'Lilita One',cursive; letter-spacing:2px;
  cursor:pointer; z-index:62;
  box-shadow:0 6px 28px rgba(255,107,53,.55), 0 2px 0 rgba(0,0,0,.25);
  transition:transform .1s, box-shadow .1s;
  animation:glowPulse 2.2s ease-in-out infinite;
}
.swing-btn:active  { transform:translateX(-50%) scale(.91); box-shadow:0 2px 8px rgba(255,107,53,.4); }
.swing-btn:disabled{ opacity:.35; cursor:not-allowed; animation:none; }

/* ── TAP POTS INSTRUCTION ── */
.tap-instruction {
  position:absolute; bottom:90px; left:50%; transform:translateX(-50%);
  background:rgba(105,240,174,.15); border:1.5px solid rgba(105,240,174,.4);
  border-radius:20px; padding:.4rem 1.2rem;
  font-family:'Lilita One',cursive; font-size:.95rem; color:#69F0AE;
  letter-spacing:1px; z-index:62; white-space:nowrap;
  animation:slideUp .35s ease-out both;
}

/* ── RESULT OVERLAY ── */
.result-overlay {
  position:absolute; inset:0; z-index:68;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:.6rem; background:rgba(0,0,0,.5); backdrop-filter:blur(3px);
  pointer-events:none;
}
.result-stamp {
  font-family:'Lilita One',cursive;
  font-size:clamp(2rem,9vw,3rem); text-align:center; line-height:1;
  text-shadow:0 4px 18px rgba(0,0,0,.6);
  animation:stampIn .5s cubic-bezier(.34,1.56,.64,1) both;
}
.result-si {
  font-family:'Noto Sans Sinhala',sans-serif; font-weight:700;
  font-size:1.05rem; color:rgba(255,255,255,.8);
  animation:slideUp .4s .15s both;
}
.result-pts {
  font-family:'Lilita One',cursive; font-size:1.4rem;
  animation:slideUp .4s .28s both;
  text-shadow:0 2px 10px rgba(0,0,0,.5);
}

/* ── CONFETTI ── */
.confetti { position:absolute; pointer-events:none; z-index:69; border-radius:3px; animation:confettiFall ease-out forwards; }

/* ── FLOAT SCORE ── */
.float-score { position:absolute; pointer-events:none; z-index:72; font-family:'Lilita One',cursive; font-size:1.7rem; text-shadow:0 2px 10px rgba(0,0,0,.7); animation:floatUp 1s ease-out forwards; }

/* ── MENU SCREEN ── */
.menu {
  width:100%; height:100dvh;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:.85rem; padding:1.4rem 1.2rem; position:relative; z-index:5;
}
.menu-title {
  font-family:'Lilita One',cursive;
  font-size:clamp(2rem,9vw,3rem); color:#FFD700; text-align:center; line-height:1;
  text-shadow:0 0 24px #FF6B35, 0 4px 0 rgba(0,0,0,.4);
  animation:glowPulse 2.5s ease-in-out infinite;
}
.menu-si {
  font-family:'Noto Sans Sinhala',sans-serif; font-weight:900;
  font-size:clamp(1rem,4.5vw,1.5rem); color:rgba(255,228,100,.85); text-align:center;
}
.menu-pots { display:flex; gap:.5rem; font-size:2.8rem; filter:drop-shadow(0 0 10px #FFD700); animation:heartbeat 2.5s ease-in-out infinite; }
.menu-desc {
  background:rgba(0,0,0,.38); border:1px solid rgba(255,215,0,.18);
  border-radius:14px; padding:.65rem 1rem;
  color:rgba(255,240,180,.82); font-size:.78rem; text-align:center; line-height:1.65; max-width:310px;
}
.diff-grid { display:flex; flex-direction:column; gap:.5rem; width:100%; max-width:330px; }
.diff-card {
  background:rgba(255,255,255,.07); border:2px solid rgba(255,255,255,.12);
  border-radius:16px; padding:.65rem .9rem; cursor:pointer;
  display:flex; align-items:center; gap:.7rem; transition:all .2s;
  position:relative; overflow:hidden;
}
.diff-card::before {
  content:''; position:absolute; inset:0; opacity:0; transition:opacity .2s;
  background:linear-gradient(135deg,rgba(255,215,0,.08),rgba(255,107,53,.06));
}
.diff-card:hover::before, .diff-card.selected::before { opacity:1; }
.diff-card.selected { border-color:var(--dc); box-shadow:0 0 16px var(--dc-glow), inset 0 0 20px rgba(255,255,255,.03); transform:scale(1.02); }
.diff-card:hover:not(.selected) { transform:scale(1.01); border-color:rgba(255,255,255,.25); }
.dc-emoji { font-size:2rem; flex-shrink:0; }
.dc-info  { flex:1; }
.dc-top   { display:flex; align-items:center; gap:.5rem; }
.dc-label { font-family:'Lilita One',cursive; font-size:1.15rem; }
.dc-si    { font-family:'Noto Sans Sinhala',sans-serif; font-weight:700; font-size:.75rem; opacity:.75; }
.dc-hint  { font-size:.65rem; color:rgba(255,255,255,.5); margin-top:.15rem; letter-spacing:.5px; }
.dc-hint-si{ font-family:'Noto Sans Sinhala',sans-serif; font-size:.62rem; color:rgba(255,255,255,.38); }
.dc-pots  { display:flex; gap:2px; align-items:center; flex-shrink:0; }
.dc-pot   { font-size:.7rem; }
.play-btn {
  background:linear-gradient(135deg,#FFD700,#FF6B35);
  color:#1a0010; border:none; border-radius:50px; padding:1rem 3.5rem;
  font-size:1.2rem; font-family:'Lilita One',cursive; cursor:pointer;
  letter-spacing:2px; box-shadow:0 0 28px rgba(255,215,0,.5),0 6px 20px rgba(0,0,0,.4);
  transition:transform .12s; animation:glowPulse 2s ease-in-out infinite;
}
.play-btn:active { transform:scale(.95); }
.hs-row { font-size:.72rem; color:rgba(255,255,255,.3); letter-spacing:2px; font-family:'Exo 2',sans-serif; }

/* ── GAME OVER ── */
.gameover {
  width:100%; height:100dvh;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:.85rem; padding:1.5rem; z-index:5; position:relative;
  background:linear-gradient(160deg,rgba(5,15,2,.97),rgba(15,5,2,.97));
}
.go-title { font-family:'Lilita One',cursive; font-size:clamp(1.6rem,7vw,2.3rem); color:#FFD700; text-align:center; text-shadow:0 0 20px #FF6B35; animation:glowPulse 2s infinite; }
.go-card  { background:rgba(255,255,255,.06); border:1px solid rgba(255,215,0,.22); border-radius:22px; padding:1.4rem 1.8rem; width:100%; max-width:290px; text-align:center; animation:slideUp .5s both; }
.go-score { font-family:'Lilita One',cursive; font-size:3.8rem; background:linear-gradient(135deg,#FFD700,#FF6B35); -webkit-background-clip:text; -webkit-text-fill-color:transparent; line-height:1; }
.go-grid  { display:grid; grid-template-columns:1fr 1fr; gap:.4rem; margin-top:.85rem; }
.go-stat  { background:rgba(255,255,255,.04); border-radius:10px; padding:.4rem; }
.go-sl    { font-size:.43rem; color:rgba(255,255,255,.32); letter-spacing:2px; }
.go-sv    { font-size:.95rem; color:#fff; font-weight:700; font-family:'Exo 2',sans-serif; }
.new-rec  { color:#FF1493; font-size:.78rem; letter-spacing:2px; display:block; margin-top:.4rem; animation:heartbeat 1s infinite; font-family:'Lilita One',cursive; }
.btn-row  { display:flex; gap:.6rem; width:100%; max-width:290px; }
.btn-a    { flex:1; background:linear-gradient(135deg,#FFD700,#FF6B35); color:#1a0010; border:none; padding:.85rem; border-radius:40px; font-size:.9rem; font-family:'Lilita One',cursive; cursor:pointer; letter-spacing:1px; transition:transform .12s; }
.btn-b    { flex:1; background:rgba(255,255,255,.09); color:rgba(255,255,255,.82); border:1px solid rgba(255,255,255,.2); padding:.85rem; border-radius:40px; font-size:.9rem; font-family:'Exo 2',sans-serif; cursor:pointer; transition:transform .12s; }
.btn-a:active, .btn-b:active { transform:scale(.95); }
`;

// ─────────────────────────────────────────────────────────────────────────────
//  SCENE BACKGROUND
// ─────────────────────────────────────────────────────────────────────────────
function SceneBG({ diff }) {
  const cfg = DIFFICULTIES[diff];
  const isHard = diff === "hard";
  return (
    <div style={{ position:"absolute", inset:0, zIndex:0, background: cfg.bg, overflow:"hidden" }}>
      {/* Sun / Moon */}
      {!isHard
        ? <div style={{ position:"absolute", top:"7%", right:"9%", width:46, height:46, borderRadius:"50%", background:"radial-gradient(circle,#FFE566,#FFA500)", boxShadow:"0 0 0 8px rgba(255,210,50,.22),0 0 0 18px rgba(255,210,50,.1)", animation:"heartbeat 3s ease-in-out infinite" }}/>
        : <div style={{ position:"absolute", top:"7%", right:"9%", width:40, height:40, borderRadius:"50%", background:"radial-gradient(circle,#dde8ff,#a0b8e8)", boxShadow:"0 0 0 6px rgba(180,200,255,.15),0 0 0 14px rgba(180,200,255,.07)", animation:"heartbeat 4s ease-in-out infinite" }}/>
      }
      {/* Stars for hard mode */}
      {isHard && [["12%","10%"],["25%","18%"],["70%","8%"],["55%","22%"],["85%","15%"],["40%","5%"]].map(([l,t],i)=>(
        <div key={i} style={{ position:"absolute", left:l, top:t, width:3+i%2*2, height:3+i%2*2, borderRadius:"50%", background:"#fff", opacity:.7+i*.05, animation:`heartbeat ${2+i*.4}s ease-in-out ${i*.3}s infinite` }}/>
      ))}
      {/* Clouds */}
      {!isHard && [[12,7,80,16],[50,4,55,12],[32,14,40,10]].map(([l,t,w,h],i)=>(
        <div key={i} style={{ position:"absolute", left:`${l}%`, top:`${t}%`, width:w, height:h, background:"rgba(255,255,255,.82)", borderRadius:20, animation:`cloudDrift ${4+i}s ease-in-out ${i*.9}s infinite alternate` }}/>
      ))}
      {/* Trees */}
      {[[4,54],[82,50],[92,57],[1,60]].map(([l,b],i)=>(
        <div key={i} style={{ position:"absolute", left:`${l}%`, bottom:`${b}%`, fontSize:`${1.6+i*.25}rem`, animation:`treeWave ${3+i*.6}s ease-in-out ${i*.5}s infinite`, transformOrigin:"bottom center", zIndex:1, filter: isHard ? "brightness(0.5)" : "none" }}>🌴</div>
      ))}
      {/* Ground */}
      <div style={{ position:"absolute", bottom:0, left:0, right:0, height:"38%", background: isHard ? "linear-gradient(180deg,#1a3a0a,#0a1a05)" : "linear-gradient(180deg,#5a9a2a,#2d5a1b)", borderTop:`3px solid ${isHard?"#2a5a10":"#6ab82a"}`, zIndex:1 }}/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  CONFETTI
// ─────────────────────────────────────────────────────────────────────────────
function Confetti() {
  const pieces = Array.from({length:22},(_,i)=>({
    id:i, left:`${8+Math.random()*84}%`,
    bg:["#FFD700","#FF6B35","#FF1493","#4CAF50","#00E5FF","#FF5722","#AB47BC"][i%7],
    dur:.65+Math.random()*.85, delay:Math.random()*.5, size:5+Math.random()*7,
  }));
  return <>{pieces.map(p=>(
    <div key={p.id} className="confetti" style={{ left:p.left, top:"8%", width:p.size, height:p.size, background:p.bg, animationDuration:`${p.dur}s`, animationDelay:`${p.delay}s`, borderRadius:Math.random()>.5?"50%":"3px" }}/>
  ))}</>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  SVG POT  — drawn purely in SVG so it looks like a clay pot
// ─────────────────────────────────────────────────────────────────────────────
function SvgPot({ cx, ropeTopY, ropeLenPx, swayAngle, broken, showPrize, isPeek, tappable, onTap, glow, diff }) {
  const potW = diff === "hard" ? 38 : diff === "medium" ? 44 : 50;
  const potH = diff === "hard" ? 38 : diff === "medium" ? 44 : 50;
  const bodyY = ropeTopY + ropeLenPx;

  return (
    <g
      transform={`rotate(${swayAngle}, ${cx}, ${ropeTopY})`}
      style={{ cursor: tappable ? "pointer" : "default" }}
      onPointerDown={tappable ? onTap : undefined}
    >
      {/* Rope */}
      {Array.from({length: Math.floor(ropeLenPx/8)}, (_,i) => (
        <line key={i}
          x1={cx} y1={ropeTopY + i*8}
          x2={cx} y2={ropeTopY + i*8 + 6}
          stroke={i%2===0?"#9B7A2A":"#C8A84B"} strokeWidth={i%3===0?3.5:2.5} strokeLinecap="round"
        />
      ))}
      {/* Rope knot at pot */}
      <circle cx={cx} cy={bodyY-4} r={4} fill="#8B6914" stroke="#6B4A0A" strokeWidth={1}/>

      {!broken ? (
        <>
          {/* Pot rim */}
          <ellipse cx={cx} cy={bodyY+5} rx={potW/2+3} ry={7}
            fill="#C8860A" stroke="#7A4A00" strokeWidth={1.5}/>
          {/* Pot body */}
          <ellipse cx={cx} cy={bodyY+potH/2+3} rx={potW/2} ry={potH/2}
            fill="url(#clayGrad)" stroke="#7A4A00" strokeWidth={1.5}/>
          {/* Pot belly bulge */}
          <ellipse cx={cx} cy={bodyY+potH/2+3} rx={potW/2+4} ry={potH/3}
            fill="none" stroke="rgba(255,255,255,.1)" strokeWidth={1}/>
          {/* Shine */}
          <ellipse cx={cx-potW/5} cy={bodyY+potH/3} rx={potW/6} ry={potH/4}
            fill="rgba(255,255,255,.22)" style={{filter:"blur(1px)"}}/>
          {/* Pot base */}
          <ellipse cx={cx} cy={bodyY+potH+5} rx={potW/3} ry={6}
            fill="#7A4A00" opacity={.8}/>
          {/* Peek highlight — subtle glow only, no ring, no gift */}
          {isPeek && (
            <>
              <circle cx={cx} cy={bodyY+potH/2+3} r={potW/2+10}
                fill="rgba(255,215,0,.22)" style={{animation:"correctRing .8s ease-out infinite"}}/>
              <circle cx={cx} cy={bodyY+potH/2+3} r={potW/2+18}
                fill="rgba(255,215,0,.08)" style={{animation:"correctRing 1.1s .2s ease-out infinite"}}/>
            </>
          )}

          {/* Glow when correct */}
          {glow==="correct" && (
            <circle cx={cx} cy={bodyY+potH/2+3} r={potW/2+16}
              fill="rgba(76,175,80,.25)" style={{animation:"correctRing .6s ease-out"}}/>
          )}
          {glow==="wrong" && (
            <circle cx={cx} cy={bodyY+potH/2+3} r={potW/2+10}
              fill="rgba(244,67,54,.22)"/>
          )}
        </>
      ) : (
        /* Broken shards */
        <>
          {[[-22,18,40],[-8,28,15],[14,20,30],[24,12,20],[0,32,55],[-16,8,25]].map(([dx,dy,rot],i)=>(
            <g key={i} transform={`translate(${cx+dx*(1+i*.15)},${bodyY+dy*(1+i*.1)}) rotate(${rot+i*55})`} opacity={.85-i*.06}>
              <polygon points="0,-10 8,6 -8,6" fill={i%2===0?"#A0522D":"#C8860A"} stroke="#6B3A00" strokeWidth={.8}/>
            </g>
          ))}
          {/* dust cloud */}
          <circle cx={cx} cy={bodyY+potH/2} r={22} fill="rgba(160,82,45,.3)" style={{animation:"potBreak .55s ease-out forwards"}}/>
          {/* prize pop */}
          {showPrize && (
            <text x={cx} y={bodyY} textAnchor="middle" fontSize={26}
              style={{animation:"floatUp 1.1s ease-out forwards", filter:"drop-shadow(0 0 8px gold)"}}>🎁</text>
          )}
        </>
      )}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PLAYER CHARACTER + STICK
// ─────────────────────────────────────────────────────────────────────────────
function Player({ stickAngle, blindfolded }) {
  const px = PLAYER_X, py = PLAYER_Y;
  return (
    <g>
      {/* Legs */}
      <line x1={px-8} y1={py+20} x2={px-14} y2={py+54} stroke="#5C3317" strokeWidth={8} strokeLinecap="round"/>
      <line x1={px+8} y1={py+20} x2={px+14} y2={py+54} stroke="#5C3317" strokeWidth={8} strokeLinecap="round"/>
      <ellipse cx={px-16} cy={py+56} rx={9} ry={5} fill="#4A2800"/>
      <ellipse cx={px+16} cy={py+56} rx={9} ry={5} fill="#4A2800"/>
      {/* Body */}
      <ellipse cx={px} cy={py+4} rx={21} ry={27} fill="url(#bodyGrad)" stroke="#C8860A" strokeWidth={1.5}/>
      {/* Shirt details */}
      <path d={`M ${px-10},${py-8} Q ${px},${py} ${px+10},${py-8}`} stroke="rgba(120,60,10,.3)" strokeWidth={2} fill="none"/>
      {/* Arms */}
      <line x1={px+19} y1={py-6} x2={px+36} y2={py-16} stroke="#FFD0A0" strokeWidth={9} strokeLinecap="round"/>
      <line x1={px-19} y1={py-4} x2={px-33} y2={py+9} stroke="#FFD0A0" strokeWidth={9} strokeLinecap="round"/>
      {/* Neck */}
      <rect x={px-7} y={py-23} width={14} height={10} rx={4} fill="#FFD0A0"/>
      {/* Head */}
      <ellipse cx={px} cy={py-33} rx={17} ry={16} fill="#FFD0A0" stroke="#C8860A" strokeWidth={1.5}/>
      {/* Hair */}
      <ellipse cx={px} cy={py-47} rx={16} ry={6} fill="#2a1200"/>
      <path d={`M ${px-16},${py-44} Q ${px-10},${py-52} ${px},${py-50} Q ${px+10},${py-52} ${px+16},${py-44}`} fill="#2a1200"/>
      {/* Face */}
      {blindfolded ? (
        <>
          <rect x={px-18} y={py-41} width={36} height={12} rx={5} fill="#1a0010" stroke="#FFD700" strokeWidth={1.5}/>
          <text x={px} y={py-33} textAnchor="middle" fontSize={8} fill="#FFD700">👀</text>
        </>
      ) : (
        <>
          <ellipse cx={px-6} cy={py-35} rx={3.5} ry={4} fill="#fff"/>
          <ellipse cx={px+6} cy={py-35} rx={3.5} ry={4} fill="#fff"/>
          <circle cx={px-5} cy={py-34} r={2} fill="#2a1200"/>
          <circle cx={px+7} cy={py-34} r={2} fill="#2a1200"/>
        </>
      )}
      <path d={`M ${px-6},${py-23} Q ${px},${py-18} ${px+6},${py-23}`} stroke="#C8860A" strokeWidth={2} fill="none" strokeLinecap="round"/>

      {/* Stick */}
      <g transform={`translate(${PIVOT_X},${PIVOT_Y}) rotate(${stickAngle})`}>
        <rect x={-4} y={-STICK_LEN} width={8} height={STICK_LEN} rx={3}
          fill="url(#stickGrad2)" stroke="#5C3317" strokeWidth={1.5}/>
        {/* grip tape */}
        {[0,.15,.3].map((f,i)=>(
          <rect key={i} x={-4} y={-STICK_LEN*(.08+f)} width={8} height={STICK_LEN*.05} rx={2} fill="#C49A2A" opacity={.7}/>
        ))}
        {/* tip glow when swinging hard */}
        <circle cx={0} cy={-STICK_LEN} r={6} fill="rgba(255,215,0,.35)" style={{animation:"heartbeat .4s ease-in-out infinite"}}/>
      </g>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN GAME COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function KanaMuttiya() {
  const [screen,      setScreen]      = useState("menu");
  const [diff,        setDiff]        = useState("easy");
  const [score,       setScore]       = useState(0);
  const [lives,       setLives]       = useState(3);
  const [round,       setRound]       = useState(0);
  const [streak,      setStreak]      = useState(0);
  const [maxStreak,   setMaxStreak]   = useState(0);
  const [totalHits,   setTotalHits]   = useState(0);

  // game state
  const [phase,       setPhase]       = useState("idle");
  // phase: idle | peek | shuffle | blind | choose | result | next
  const [potOrder,    setPotOrder]    = useState([]);   // array of pot indices, shows positions
  const [prizePos,    setPrizePos]    = useState(0);    // which position (0-based) has the prize
  const [potAngles,   setPotAngles]   = useState([]);   // sway angles
  const [brokenPos,   setBrokenPos]   = useState(-1);   // which position was smashed
  const [blindCount,  setBlindCount]  = useState(0);
  const [resultData,  setResultData]  = useState(null);
  const [showConfetti,setShowConfetti]= useState(false);
  const [floats,      setFloats]      = useState([]);
  const [glows,       setGlows]       = useState({});   // pos -> "correct"|"wrong"

  const [highScores,  setHighScores]  = useState(()=>JSON.parse(localStorage.getItem("km_hs2")||"{}"));
  const [newRecord,   setNewRecord]   = useState(false);
  const [stats,       setStats]       = useState({});

  // refs
  const phaseRef    = useRef("idle");
  const scoreRef    = useRef(0);
  const livesRef    = useRef(3);
  const roundRef    = useRef(0);
  const streakRef   = useRef(0);
  const maxStrkRef  = useRef(0);
  const hitsRef     = useRef(0);
  const rafRef      = useRef(null);
  const stickRef    = useRef(-MAX_SWING);
  const swingDirRef = useRef(1);
  const potAngRef   = useRef([]);
  const potDirRef   = useRef([]);
  const canChooseRef= useRef(false);
  const prizeRef    = useRef(0);

  const cfg = DIFFICULTIES[diff];

  // ── STICK ANIMATION LOOP ──
  const loop = useCallback(() => {
    const ph = phaseRef.current;
    if (ph === "blind") { rafRef.current = requestAnimationFrame(loop); return; }

    // stick swing
    const spd = ph === "choose" ? cfg.swingSpeed * 1.8 : cfg.swingSpeed * .9;
    stickRef.current += swingDirRef.current * spd;
    if (stickRef.current >  MAX_SWING) { stickRef.current =  MAX_SWING; swingDirRef.current = -1; }
    if (stickRef.current < -MAX_SWING) { stickRef.current = -MAX_SWING; swingDirRef.current =  1; }

    // pot sway
    const na = potAngRef.current.map((a, i) => {
      const amp = cfg.potSwayAmp;
      const spd2 = (ph === "shuffle" ? 2.2 : .55) * (1 + i * .12);
      let na = a + potDirRef.current[i] * spd2;
      if (na >  amp) { na =  amp; potDirRef.current[i] = -1; }
      if (na < -amp) { na = -amp; potDirRef.current[i] =  1; }
      return na;
    });
    potAngRef.current = na;
    setPotAngles([...na]);

    rafRef.current = requestAnimationFrame(loop);
  }, [cfg]);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [loop]);

  // ── SHUFFLE ANIMATION ──
  const doShuffle = useCallback(async (order, prize, cfg) => {
    let cur = [...order];
    let prizeP = prize;
    phaseRef.current = "shuffle";
    setPhase("shuffle");

    for (let s = 0; s < cfg.swapCount; s++) {
      await sleep(cfg.swapSpeed);
      // pick two random positions to swap
      let a = Math.floor(Math.random() * cur.length);
      let b;
      do { b = Math.floor(Math.random() * cur.length); } while (b === a);
      // swap
      [cur[a], cur[b]] = [cur[b], cur[a]];
      // track where prize went
      if (prizeP === a) prizeP = b;
      else if (prizeP === b) prizeP = a;
      setPotOrder([...cur]);
      setPrizePos(prizeP);
      prizeRef.current = prizeP;
    }
    return { order: cur, prize: prizeP };
  }, []);

  // ── RUN ONE ROUND ──
  const runRound = useCallback(async () => {
    if (phaseRef.current === "dead") return;
    const numPots = cfg.pots;

    // fresh state
    const order = Array.from({length: numPots}, (_,i) => i);
    const prize = Math.floor(Math.random() * numPots);
    setPotOrder(order); setPrizePos(prize); prizeRef.current = prize;
    setBrokenPos(-1); setResultData(null); setShowConfetti(false); setGlows({});
    canChooseRef.current = false;

    // init sway
    const initA = Array.from({length: numPots}, (_,i) => (i%2===0?1:-1)*rand(2,8));
    const initD = Array.from({length: numPots}, () => Math.random()>.5?1:-1);
    potAngRef.current = initA; potDirRef.current = initD;
    setPotAngles([...initA]);

    // READY — show all plain pots first, nothing highlighted
    phaseRef.current = "ready";
    setPhase("ready");
    await sleep(800);

    // PEEK — highlight which pot has the prize (no gift shown)
    phaseRef.current = "peek";
    setPhase("peek");
    await sleep(cfg.peekMs);

    // SHUFFLE
    const { order: newOrder, prize: newPrize } = await doShuffle(order, prize, cfg);

    // BLIND (medium/hard)
    if (cfg.blindMs > 0) {
      phaseRef.current = "blind";
      setPhase("blind");
      let cnt = Math.ceil(cfg.blindMs / 1000);
      setBlindCount(cnt);
      for (let i = cnt; i > 0; i--) {
        setBlindCount(i);
        await sleep(1000);
      }
    }

    // CHOOSE
    phaseRef.current = "choose";
    setPhase("choose");
    canChooseRef.current = true;
  }, [cfg, doShuffle]);

  // ── TAP A POT ──
  const handlePotTap = useCallback(async (posIdx) => {
    if (!canChooseRef.current) return;
    if (phaseRef.current !== "choose") return;
    canChooseRef.current = false;
    phaseRef.current = "result";
    setPhase("result");

    const correct = posIdx === prizeRef.current;
    setBrokenPos(posIdx);

    // Show correct pot too if wrong
    setGlows({ [posIdx]: correct ? "correct" : "wrong", ...(correct ? {} : { [prizeRef.current]: "correct" }) });

    let pts = 0;
    if (correct) {
      streakRef.current++;
      if (streakRef.current > maxStrkRef.current) maxStrkRef.current = streakRef.current;
      hitsRef.current++;
      pts = cfg.scorePerHit + (streakRef.current - 1) * 50;
      setShowConfetti(true);
      setResultData({ correct:true, pts, text:"🎉 SMASHED IT!", si:"ජය ගත්තා!" });
    } else {
      streakRef.current = 0;
      pts = -30;
      const newLives = Math.max(0, livesRef.current - 1);
      livesRef.current = newLives;
      setLives(newLives);
      setResultData({ correct:false, pts, text:"💥 WRONG POT!", si:"වැරදි මුට්ටිය!" });
    }

    scoreRef.current = Math.max(0, scoreRef.current + pts);
    setScore(scoreRef.current);
    setStreak(streakRef.current);
    setMaxStreak(maxStrkRef.current);
    setTotalHits(hitsRef.current);

    // Floating score
    const fid = uid();
    setFloats(p=>[...p,{id:fid, pts}]);
    setTimeout(()=>setFloats(p=>p.filter(f=>f.id!==fid)), 1000);

    await sleep(2000);
    setShowConfetti(false);

    roundRef.current++;
    setRound(roundRef.current);

    if (livesRef.current <= 0 || roundRef.current >= cfg.rounds) {
      endGame();
    } else {
      await runRound();
    }
  }, [cfg, runRound]);

  // ── START GAME ──
  const startGame = useCallback(async () => {
    scoreRef.current = 0; livesRef.current = 3; roundRef.current = 0;
    streakRef.current = 0; maxStrkRef.current = 0; hitsRef.current = 0;
    stickRef.current = -MAX_SWING; swingDirRef.current = 1;
    setScore(0); setLives(3); setRound(0); setStreak(0); setMaxStreak(0); setTotalHits(0);
    setNewRecord(false); setResultData(null); setBrokenPos(-1);
    phaseRef.current = "idle";
    setScreen("playing");
    await sleep(300);
    await runRound();
  }, [runRound]);

  // ── END GAME ──
  const endGame = useCallback(() => {
    phaseRef.current = "dead";
    const final = scoreRef.current;
    const prev = highScores[diff] || 0;
    const isNew = final > prev;
    if (isNew) {
      const updated = {...highScores, [diff]: final};
      setHighScores(updated);
      localStorage.setItem("km_hs2", JSON.stringify(updated));
      setNewRecord(true);
    }
    setStats({ score: final, hits: hitsRef.current, rounds: roundRef.current, maxStreak: maxStrkRef.current, accuracy: roundRef.current ? Math.round(hitsRef.current/roundRef.current*100) : 0 });
    setScreen("gameover");
  }, [diff, highScores]);

  // ── STICK TIP for hit detection ── (not needed now — user taps pots directly)
  const stickAngle = stickRef.current;

  // ── POT Y positions based on diff (more pots = higher up) ──
  const ropeTopY = BEAM_Y;
  const ropeLenPx = diff === "hard" ? 90 : diff === "medium" ? 100 : 110;
  const potXsList = POT_XS[cfg.pots];

  const phaseLabel = {
    ready:   { text:"🏺 Get Ready...",         si:"සූදානම් වන්න!",              color:"#fff",     bg:"rgba(255,255,255,.85)",  textCol:"#1a0010" },
    peek:    { text:"👀 Remember This Pot!",   si:"මේ මුට්ටිය මතක තබා ගන්න!", color:"#FFD700",  bg:"rgba(255,215,0,.9)",     textCol:"#1a0010" },
    shuffle: { text:"🔀 Watch Carefully...",   si:"හොඳින් බලන්න!",             color:"#FF9800",  bg:"rgba(255,152,0,.88)",    textCol:"#1a0010" },
    choose:  { text:"👆 Tap the Right Pot!",   si:"නිවැරදි මුට්ටිය තට්ටු කරන්න!", color:"#69F0AE", bg:"rgba(40,200,100,.88)", textCol:"#0a2a10" },
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="root">

        {/* ── MENU ── */}
        {screen === "menu" && (
          <div className="canvas" style={{background:"linear-gradient(180deg,#1a3a10 0%,#0a1a05 100%)"}}>
            <SceneBG diff={diff}/>
            <div className="menu">
              <div className="menu-title">Kana Muttiya</div>
              <div className="menu-si">කන මුට්ටිය 🏺</div>
              <div className="menu-pots">🏺🏺🏺</div>
              <div className="menu-desc">
                👀 See which pot hides the prize 🎁<br/>
                🔀 Watch them shuffle around<br/>
                👆 <strong>Tap</strong> the right pot to smash it!<br/>
                🔥 Streak bonuses for every correct hit!
              </div>

              {/* Difficulty cards */}
              <div className="diff-grid">
                {Object.values(DIFFICULTIES).map(d => (
                  <div
                    key={d.key}
                    className={`diff-card ${diff===d.key?"selected":""}`}
                    style={{"--dc":d.color,"--dc-glow":`${d.color}55`}}
                    onClick={()=>setDiff(d.key)}
                  >
                    <div className="dc-emoji">{d.emoji}</div>
                    <div className="dc-info">
                      <div className="dc-top">
                        <div className="dc-label" style={{color: diff===d.key ? d.color : "#fff"}}>{d.label}</div>
                        <div className="dc-si">{d.si}</div>
                      </div>
                      <div className="dc-hint">{d.hint}</div>
                      <div className="dc-hint-si">{d.hintSi}</div>
                    </div>
                    <div className="dc-pots">
                      {Array.from({length:d.pots},(_,i)=><span key={i} className="dc-pot">🏺</span>)}
                    </div>
                  </div>
                ))}
              </div>

              <button className="play-btn" onClick={startGame}>🥁 PLAY NOW!</button>
              {highScores[diff] > 0 && (
                <div className="hs-row">{diff.toUpperCase()} BEST: {highScores[diff]} pts</div>
              )}
            </div>
          </div>
        )}

        {/* ── PLAYING ── */}
        {screen === "playing" && (
          <div className="canvas">
            <SceneBG diff={diff}/>

            {/* HUD */}
            <div className="hud">
              <div className="hud-score">
                <div className="hud-slbl">SCORE</div>
                <div className="hud-sval">{score}</div>
              </div>
              <div className="hud-lives">
                {[0,1,2].map(i=>(
                  <span key={i} style={{fontSize:"1.05rem",opacity:i<lives?1:.15,filter:i<lives?"drop-shadow(0 0 4px red)":"none",animation:lives===1&&i===0?"heartbeat 1s infinite":"none"}}>❤️</span>
                ))}
              </div>
              {streak>1&&<div className="streak-badge"><div className="streak-v">🔥×{streak}</div></div>}
              <div className="hud-right">
                <div className="hud-rlbl">ROUND</div>
                <div className="hud-rval">{round+1}/{cfg.rounds}</div>
              </div>
              <div className="hud-right" style={{background:`rgba(${diff==="easy"?"76,175,80":diff==="medium"?"255,152,0":"244,67,54"},.15)`,borderColor:`rgba(${diff==="easy"?"76,175,80":diff==="medium"?"255,152,0":"244,67,54"},.3)`}}>
                <div className="hud-rlbl">MODE</div>
                <div className="hud-rval" style={{color:cfg.color}}>{cfg.si}</div>
              </div>
            </div>

            {/* SVG Game Scene */}
            <svg
              width="100%" height="100%"
              viewBox={`0 0 ${GAME_W} ${GAME_H}`}
              style={{position:"absolute",top:0,left:0,zIndex:10,touchAction:"none"}}
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <linearGradient id="clayGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#D4956A"/>
                  <stop offset="45%" stopColor="#A0522D"/>
                  <stop offset="100%" stopColor="#6B3A00"/>
                </linearGradient>
                <linearGradient id="bodyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFE0A0"/>
                  <stop offset="100%" stopColor="#C8860A"/>
                </linearGradient>
                <linearGradient id="stickGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#7B4A20"/>
                  <stop offset="50%" stopColor="#C49A2A"/>
                  <stop offset="100%" stopColor="#7B4A20"/>
                </linearGradient>
              </defs>

              {/* Main beam */}
              <rect x={20} y={0} width={GAME_W-40} height={BEAM_Y} rx={5}
                fill="url(#stickGrad2)" stroke="#5C3317" strokeWidth={2}/>
              <rect x={20} y={0} width={GAME_W-40} height={8} rx={4}
                fill="rgba(255,255,255,.18)"/>
              {/* Poles */}
              <rect x={20} y={0} width={14} height={140} rx={4} fill="#7B4A20" stroke="#5C3317" strokeWidth={1.5}/>
              <rect x={GAME_W-34} y={0} width={14} height={140} rx={4} fill="#7B4A20" stroke="#5C3317" strokeWidth={1.5}/>

              {/* Pots */}
              {potOrder.map((originalIdx, posIdx) => (
                <SvgPot
                  key={posIdx}
                  cx={potXsList[posIdx]}
                  ropeTopY={ropeTopY}
                  ropeLenPx={ropeLenPx}
                  swayAngle={potAngles[posIdx] || 0}
                  broken={brokenPos === posIdx}
                  showPrize={
                    (brokenPos === posIdx && prizePos === posIdx) ||
                    (phase === "result" && prizePos === posIdx && brokenPos !== prizePos)
                  }
                  isPeek={phase === "peek" && prizePos === posIdx}
                  tappable={phase === "choose" && brokenPos === -1}
                  onTap={() => handlePotTap(posIdx)}
                  glow={glows[posIdx] || null}
                  diff={diff}
                />
              ))}

              {/* Player */}
              <Player stickAngle={stickAngle} blindfolded={phase === "blind"}/>
            </svg>

            {/* Phase banners */}
            {phaseLabel[phase] && (
              <div className="phase-banner" style={{top:"20%"}}>
                <div className="pb-main" style={{background:phaseLabel[phase].bg, color:phaseLabel[phase].textCol}}>
                  {phaseLabel[phase].text}
                </div>
                <div className="pb-si">{phaseLabel[phase].si}</div>
              </div>
            )}

            {/* Blindfold screen */}
            {phase === "blind" && (
              <div className="blind-screen">
                <div className="blind-icon">🙈</div>
                <div className="blind-txt">BLINDFOLDED!</div>
                <div className="blind-si">ඇස් බැඳ ගත්තා!</div>
                <div className="blind-timer">{blindCount}</div>
              </div>
            )}

            {/* Result overlay */}
            {resultData && (
              <div className="result-overlay">
                {showConfetti && <Confetti/>}
                <div className="result-stamp" style={{color: resultData.correct ? "#FFD700" : "#FF4444"}}>
                  {resultData.text}
                </div>
                <div className="result-si">{resultData.si}</div>
                <div className="result-pts" style={{color: resultData.pts > 0 ? "#4CAF50" : "#F44336"}}>
                  {resultData.pts > 0 ? `+${resultData.pts}` : resultData.pts} pts
                  {resultData.correct && streak > 1 && ` · 🔥 ×${streak} streak!`}
                </div>
              </div>
            )}

            {/* Floating scores */}
            {floats.map(f=>(
              <div key={f.id} className="float-score" style={{left:"50%",top:"42%",transform:"translateX(-50%)",color:f.pts>0?"#FFD700":"#F44336"}}>
                {f.pts>0?`+${f.pts}`:f.pts}
              </div>
            ))}
          </div>
        )}

        {/* ── GAME OVER ── */}
        {screen === "gameover" && (
          <div className="canvas" style={{background:"linear-gradient(160deg,#05180A,#180503)"}}>
            <SceneBG diff={diff}/>
            <div className="gameover">
              <div style={{fontSize:"2.8rem"}}>🏺</div>
              <div className="go-title">
                {stats.accuracy===100?"Perfect! ශූරයා! 🏆": stats.accuracy>=70?"Well Done! දක්ෂ! 🎉":"සුභ අලුත් අවුරුද්දක්! 🌸"}
              </div>
              <div style={{fontFamily:"'Noto Sans Sinhala',sans-serif",color:"rgba(255,200,100,.6)",fontSize:".8rem"}}>
                සුභ අලුත් අවුරුද්දක් වේවා! 🎊
              </div>
              <div className="go-card">
                <div style={{fontSize:".48rem",color:"rgba(255,255,255,.28)",letterSpacing:3,fontFamily:"'Exo 2',sans-serif"}}>FINAL SCORE</div>
                <div className="go-score">{stats.score}</div>
                {newRecord && <span className="new-rec">🏆 NEW RECORD!</span>}
                <div className="go-grid">
                  <div className="go-stat"><div className="go-sl">CORRECT</div><div className="go-sv">{stats.hits}/{stats.rounds}</div></div>
                  <div className="go-stat"><div className="go-sl">ACCURACY</div><div className="go-sv">{stats.accuracy}%</div></div>
                  <div className="go-stat"><div className="go-sl">MAX STREAK</div><div className="go-sv">🔥{stats.maxStreak}</div></div>
                  <div className="go-stat"><div className="go-sl">{diff.toUpperCase()} BEST</div><div className="go-sv">{highScores[diff]||0}</div></div>
                </div>
              </div>
              <div className="btn-row">
                <button className="btn-a" onClick={startGame}>PLAY AGAIN</button>
                <button className="btn-b" onClick={()=>setScreen("menu")}>MENU</button>
              </div>
              <div style={{fontSize:"1.2rem",letterSpacing:5}}>🏺💥🎉🏮🎁</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}